import {
	Dialog,
	DialogContent,
	IconButton,
	Tooltip as MuiTooltip,
	TooltipProps as MuiTooltipProps,
} from '@mui/material'
import ClickAwayListener from '@mui/material/ClickAwayListener'
import React, { ReactNode, useState } from 'react'
import { useIsScreenDown } from '../../hooks/useIsScreenDown'
import Icon from '../Icon'

export interface TooltipProps
	extends Omit<MuiTooltipProps, 'title' | 'children'> {
	text?: ReactNode
	icon?: string
	title?: MuiTooltipProps['title']
	children?: MuiTooltipProps['children']
	iconSizeInTooltip?: number
	iconSizeInButton?: number
}

interface ResponsiveTooltipProps extends TooltipProps {
	children?: React.ReactElement
}

export const tooltipIconColor = {
	color: 'rgba(34, 39, 43, .5)',
}

export const tooltipComponentProps = {
	tooltip: {
		sx: {
			background: 'white',
			border: '1px solid var(--nsw-grey-01)',
			padding: '1rem',
			color: 'var(--nsw-text-dark)',
			fontSize: 'var(--nsw-font-size-xs-desktop)',
			lineHeight: 'var(--nsw-line-height-xs-desktop)',
		},
	},
}

export const Tooltip = ({
	text,
	icon = 'ic:outline-info',
	iconSizeInTooltip = 16,
	iconSizeInButton = iconSizeInTooltip,
	...props
}: TooltipProps) => {
	const [open, setOpen] = useState(false)

	const handleTooltipClose = () => {
		setOpen(false)
	}

	const handleTooltipOpen = () => {
		setOpen(true)
	}
	return (
		<ClickAwayListener onClickAway={handleTooltipClose}>
			<MuiTooltip
				disableTouchListener
				open={open}
				componentsProps={tooltipComponentProps}
				onClose={handleTooltipClose}
				onOpen={handleTooltipOpen}
				title={
					<div className="flex gap-3 Tooltip__title">
						<Icon
							width={iconSizeInTooltip}
							height={iconSizeInTooltip}
							icon={icon}
							className="flex-shrink-0 Tooltip__title-icon"
							css={tooltipIconColor}
						/>
						{!!text && (
							<div className="bold flex-1 Tooltip__title-text">
								{text}
							</div>
						)}
					</div>
				}
				{...props}
			>
				<button
					type="button"
					aria-label="Show tooltip"
					css={tooltipIconColor}
					onClick={handleTooltipOpen}
				>
					<Icon
						width={iconSizeInButton}
						height={iconSizeInButton}
						icon={icon}
					/>
				</button>
			</MuiTooltip>
		</ClickAwayListener>
	)
}

export const ResponsiveTooltip = ({
	text,
	icon = 'ic:outline-info',
	iconSizeInTooltip = 16,
	iconSizeInButton = iconSizeInTooltip,
	children,
	...props
}: ResponsiveTooltipProps) => {
	const isMobile = useIsScreenDown('md')
	const [open, setOpen] = useState(false)

	const handleOpen = () => setOpen(true)
	const handleClose = () => setOpen(false)

	// Mobile: Use Modal
	if (isMobile) {
		return (
			<>
				{children ? (
					React.cloneElement(children, { onClick: handleOpen })
				) : (
					<IconButton onClick={handleOpen} size="small">
						<Icon
							width={iconSizeInButton}
							height={iconSizeInButton}
							icon={icon}
							className="Tooltip__trigger-icon"
						/>
					</IconButton>
				)}
				<Dialog
					open={open}
					onClose={handleClose}
					maxWidth="sm"
					fullWidth
				>
					<DialogContent>
						<div className="flex gap-3 items-start">
							<Icon
								width={iconSizeInTooltip}
								height={iconSizeInTooltip}
								icon={icon}
								className="flex-shrink-0 mt-1"
								css={tooltipIconColor}
							/>
							<div className="flex-1">{text}</div>
							<IconButton
								onClick={handleClose}
								size="small"
								className="ml-2"
							>
								<Icon
									icon="material-symbols:close"
									width={16}
									height={16}
								/>
							</IconButton>
						</div>
					</DialogContent>
				</Dialog>
			</>
		)
	}

	// Desktop: Use Tooltip component
	return (
		<Tooltip
			text={text}
			icon={icon}
			iconSizeInTooltip={iconSizeInTooltip}
			iconSizeInButton={iconSizeInButton}
			{...props}
		>
			{children}
		</Tooltip>
	)
}
